import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle } from 'lucide-react';

import { apiClient } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { verifyMagicLink } from '@/api/auth.api';
import { useAuth } from '@/hooks/useAuth';

type VerifyStatus =
  | { kind: 'verifying' }
  | { kind: 'success' }
  | { kind: 'needs_onboarding'; email: string }
  | { kind: 'confirm_profile'; email: string; suggestedFirstName: string; suggestedLastName: string }
  | { kind: 'invalid' };

/**
 * True when the stored first name looks auto-derived from the email
 * rather than typed by a human — the server defaults `firstName` to the
 * email's local part on first sign-in (and invited users never get to
 * type a name before their first session). "Contains a dot" catches
 * `kate.gatev`; equality catches single-word local parts like `kate`.
 */
function looksEmailDerived(firstName: string, email: string): boolean {
  const localPart = email.split('@')[0] ?? '';
  return firstName.includes('.') || firstName.toLowerCase() === localPart.toLowerCase();
}

function capitalize(word: string): string {
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : '';
}

/** Best-guess real name from `first.last@…` style local parts. */
function suggestNameFromEmail(email: string): { firstName: string; lastName: string } {
  const localPart = email.split('@')[0] ?? '';
  const parts = localPart.split(/[._-]+/).filter(Boolean);
  return {
    firstName: capitalize(parts[0] ?? ''),
    lastName: capitalize(parts.length > 1 ? (parts[parts.length - 1] ?? '') : ''),
  };
}

/**
 * Magic-link landing page. Reads `?token=…`, exchanges it for a session
 * cookie via /api/auth/magic-link/verify, and redirects to /dashboard on
 * success (or to a "create your org" flow if the user has no memberships).
 *
 * Idempotent: if the same token is verified twice the second call returns 401
 * and we show the invalid state.
 */
export function MagicLinkVerifyPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState<VerifyStatus>({ kind: 'verifying' });

  const token = params.get('token');

  // Magic-link tokens are single-use server-side. React StrictMode
  // intentionally double-mounts effects in dev, which would consume the
  // token on the first invocation and 401 on the second — flashing the
  // "invalid" state even though sign-in actually succeeded.
  //
  // `verifiedTokenRef` survives the StrictMode remount (refs persist across
  // dev unmount/remount cycles for the same logical mount), so we only ever
  // fire `/magic-link/verify` once per token.
  const verifiedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus({ kind: 'invalid' });
      return;
    }

    if (verifiedTokenRef.current === token) {
      // Second StrictMode invocation for the same token — bail.
      return;
    }
    verifiedTokenRef.current = token;

    const tokenValue = token; // narrow for the closure — already null-checked above
    void (async () => {
      try {
        const result = await verifyMagicLink(tokenValue);
        if (result.needsOnboarding) {
          setStatus({ kind: 'needs_onboarding', email: result.user.email });
          return;
        }
        // One-time onboarding completion: invited users land here with a
        // firstName derived from their email. Ask them to confirm their
        // real name before continuing (the session cookie is already set).
        // `profileCompletedAt` is the terminal state — once anyone has
        // saved (or skipped) the interstitial, it never shows again, even
        // when their real first name equals the email local part.
        if (!result.user.profileCompletedAt && looksEmailDerived(result.user.firstName, result.user.email)) {
          const suggested = suggestNameFromEmail(result.user.email);
          setStatus({
            kind: 'confirm_profile',
            email: result.user.email,
            suggestedFirstName: suggested.firstName || result.user.firstName,
            suggestedLastName:
              suggested.lastName || (result.user.lastName === 'User' ? '' : result.user.lastName),
          });
          return;
        }
        // Pull the new session into the AuthProvider so the rest of the app sees it.
        await refresh();
        setStatus({ kind: 'success' });
      } catch {
        setStatus({ kind: 'invalid' });
      }
    })();
  }, [token, refresh]);

  if (status.kind === 'success') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-kw-bg-blue p-4">
      <Card className="w-full max-w-sm p-6 text-center sm:p-8">
        {status.kind === 'verifying' ? (
          <>
            <div className="mx-auto mb-4 size-10 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
            <h1 className="typo-h2">Signing you in…</h1>
            <p className="mt-1 text-[13px] text-kw-muted">One moment.</p>
          </>
        ) : status.kind === 'confirm_profile' ? (
          <ConfirmProfileForm
            email={status.email}
            initialFirstName={status.suggestedFirstName}
            initialLastName={status.suggestedLastName}
            onDone={() => {
              void (async () => {
                // Pull the (possibly updated) session into the AuthProvider,
                // then continue to the dashboard.
                await refresh();
                setStatus({ kind: 'success' });
              })();
            }}
          />
        ) : status.kind === 'needs_onboarding' ? (
          <>
            <div className="mx-auto mb-4 grid size-12 place-items-center rounded-kw-lg bg-kw-cerulean-light text-kw-cerulean-dark">
              <CheckCircle2 className="size-6" />
            </div>
            <h1 className="typo-h2">You&apos;re verified</h1>
            <p className="mt-2 text-[13px] text-kw-muted">
              Welcome, <strong className="text-kw-shark">{status.email}</strong>. You&apos;re not yet a member
              of an organization. Ask your administrator to add you, or set up a new organization (lands
              soon).
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-6"
              onClick={() => {
                void navigate('/login');
              }}
            >
              Back to sign in
            </Button>
          </>
        ) : (
          <>
            <div className="mx-auto mb-4 grid size-12 place-items-center rounded-kw-lg bg-kw-danger-bg text-kw-danger-text">
              <AlertTriangle className="size-6" />
            </div>
            <h1 className="typo-h2">Sign-in link invalid</h1>
            <p className="mt-2 text-[13px] text-kw-muted">
              This link may have expired, already been used, or contained a typo. Request a fresh one.
            </p>
            <Button
              size="sm"
              className="mt-6"
              onClick={() => {
                void navigate('/login');
              }}
            >
              Request a new link
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}

/**
 * One-time "confirm your name" step shown when the account's first name
 * looks auto-derived from the email. PATCHes /api/auth/profile directly
 * via the shared axios client (deliberately not the auth API module) and
 * then hands control back to the parent, which refreshes the session and
 * redirects to /dashboard. Saving is best-effort — "Skip for now" (and
 * any save failure) still lets the person in; they can edit their
 * profile later.
 */
function ConfirmProfileForm({
  email,
  initialFirstName,
  initialLastName,
  onDone,
}: {
  email: string;
  initialFirstName: string;
  initialLastName: string;
  onDone: () => void;
}) {
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || saving) return;
    setSaving(true);
    setError(null);
    void (async () => {
      try {
        await apiClient.patch('/api/auth/profile', {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        });
        onDone();
      } catch {
        setSaving(false);
        setError('Could not save your name right now. You can continue and update it later.');
      }
    })();
  }

  // Skip persists too (wave-1 review fix): saving the CURRENT names sets
  // `profileCompletedAt` server-side, so the interstitial never re-shows
  // on the next sign-in. Best-effort — a failure still lets them in.
  function handleSkip(): void {
    void (async () => {
      try {
        await apiClient.patch('/api/auth/profile', {
          firstName: (initialFirstName || 'New').trim(),
          lastName: (initialLastName || 'User').trim(),
        });
      } catch {
        // Non-fatal — they'll simply be asked again next time.
      }
      onDone();
    })();
  }

  return (
    <>
      <div className="mx-auto mb-4 grid size-12 place-items-center rounded-kw-lg bg-kw-cerulean-light text-kw-cerulean-dark">
        <CheckCircle2 className="size-6" />
      </div>
      <h1 className="typo-h2">Welcome — confirm your name</h1>
      <p className="mt-1 text-[13px] text-kw-muted">
        You&apos;re signed in as <strong className="text-kw-shark">{email}</strong>. Tell us what to call you
        before we continue.
      </p>
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="mt-5 space-y-3 text-left"
      >
        <Input
          label="First name"
          name="firstName"
          required
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
          }}
        />
        <Input
          label="Last name"
          name="lastName"
          required
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
          }}
        />
        {error ? <p className="text-[12px] text-kw-danger-text">{error}</p> : null}
        <div className="flex flex-col gap-2 pt-1">
          <Button type="submit" isLoading={saving} disabled={!firstName.trim() || !lastName.trim()}>
            Continue to dashboard
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={handleSkip}>
            Skip for now
          </Button>
        </div>
      </form>
    </>
  );
}
